import { useState, useEffect } from 'react';
import { clientsService } from '@/services/clients.service';
import { projectsService } from '@/services/projects.service';
import { invoicesService, CreateInvoiceRequest } from '@/services/invoices.service';
import { profilesService } from '@/services/profiles.service';
import type { Client, Project, LineItem, LineItemUnit, User, Invoice } from '@/types';
import { HOURS_PER_MAN_DAY } from '@/types';

// Resolve the unit price for a line item based on its unit and the client's hourly rate.
// Man days (MD) bill at the hourly rate × HOURS_PER_MAN_DAY; everything else uses the hourly rate as-is.
function getRateForUnit(unit: LineItemUnit, hourlyRate: number): number {
  if (unit === 'md') {
    return hourlyRate * HOURS_PER_MAN_DAY;
  }
  return hourlyRate;
}

interface InvoiceFormProps {
  initialData?: Partial<Invoice>;
  onSuccess?: (invoice: Invoice) => void;
  onCancel?: () => void;
  mode?: 'create' | 'edit';
  preselectedClientId?: string;
}

export function InvoiceForm({ initialData, onSuccess, onCancel, mode = 'create', preselectedClientId }: InvoiceFormProps) {
  // Helper function to get currency symbol
  const getCurrencySymbol = (currency?: string): string => {
    switch (currency) {
      case 'EUR':
        return '€';
      case 'USD':
        return '$';
      case 'AED':
        return 'AED';
      default:
        return '$';
    }
  };

  // Form state
  const [clients, setClients] = useState<Client[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [profile, setProfile] = useState<User | null>(null);
  const [loadingData, setLoadingData] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Form fields
  const [clientId, setClientId] = useState(initialData?.clientId || preselectedClientId || '');
  const [projectId, setProjectId] = useState(initialData?.projectId || '');
  const [issueDate, setIssueDate] = useState(
    initialData?.issueDate || new Date().toISOString().split('T')[0]
  );
  const [dueDate, setDueDate] = useState(
    initialData?.dueDate || getDefaultDueDate()
  );
  const [servicePeriodStart, setServicePeriodStart] = useState(initialData?.servicePeriodStart || '');
  const [servicePeriodEnd, setServicePeriodEnd] = useState(initialData?.servicePeriodEnd || '');
  const [lineItems, setLineItems] = useState<Omit<LineItem, 'id'>[]>(
    initialData?.lineItems
      ? initialData.lineItems.map(({ id, ...rest }) => ({
          description: rest.description,
          quantity: rest.quantity,
          unit: rest.unit || 'hours', // Use unit from DB or default to 'hours'
          unitPrice: rest.unitPrice,
          amount: rest.amount,
        }))
      : [{ description: '', quantity: 1, unit: 'hours', unitPrice: 0, amount: 0 }]
  );
  const [taxRate, setTaxRate] = useState<number>(0);
  const [discount, setDiscount] = useState(initialData?.discount || 0);
  const [notes, setNotes] = useState(initialData?.notes || '');
  const [termsAndConditions, setTermsAndConditions] = useState<string>('');

  // Calculated values
  const [subtotal, setSubtotal] = useState(0);
  const [taxAmount, setTaxAmount] = useState(0);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (clientId) {
      loadProjects(clientId);
      const selectedClient = clients.find((c) => c.id === clientId);

      // Auto-fill hourly rate from selected client into line items
      if (selectedClient?.hourlyRate && mode === 'create') {
        // Update existing line items with hourly rate if they have unitPrice = 0
        setLineItems((prevItems) =>
          prevItems.map((item) => {
            if (item.unitPrice === 0) {
              const unitPrice = getRateForUnit(item.unit, selectedClient.hourlyRate!);
              return {
                ...item,
                unitPrice,
                amount: item.quantity * unitPrice,
              };
            }
            return item;
          })
        );
      }

      // Set tax rate: Check VAT reverse charge first, then client defaultTaxRate, then profile defaultTaxRate, otherwise 0
      if (mode === 'create' && initialData?.taxRate === undefined) {
        if (selectedClient?.vatReverseCharge) {
          // If client has VAT reverse charge enabled, default to 0%
          setTaxRate(0);
        } else if (selectedClient?.defaultTaxRate !== undefined) {
          setTaxRate(selectedClient.defaultTaxRate);
        } else if (profile?.defaultTaxRate !== undefined) {
          setTaxRate(profile.defaultTaxRate);
        } else {
          setTaxRate(0);
        }
      }

      // Set terms and conditions: Check client termsAndConditions first, then profile, otherwise default
      if (mode === 'create' && !initialData?.termsAndConditions) {
        if (selectedClient?.termsAndConditions) {
          setTermsAndConditions(selectedClient.termsAndConditions);
        } else if (profile?.defaultTermsAndConditions) {
          setTermsAndConditions(profile.defaultTermsAndConditions);
        } else {
          setTermsAndConditions(getDefaultTerms());
        }
      }
    } else {
      setProjects([]);
      setProjectId('');
    }
  }, [clientId, clients, mode, profile]);

  useEffect(() => {
    calculateTotals();
  }, [lineItems, taxRate, discount]);

  // Set initial values from initialData if editing
  useEffect(() => {
    if (initialData?.taxRate !== undefined) {
      setTaxRate(initialData.taxRate);
    }
    if (initialData?.termsAndConditions) {
      setTermsAndConditions(initialData.termsAndConditions);
    }
  }, [initialData?.taxRate, initialData?.termsAndConditions]);

  function getDefaultDueDate(): string {
    const date = new Date();
    date.setDate(date.getDate() + 30); // 30 days from today
    return date.toISOString().split('T')[0];
  }

  function getDefaultTerms(): string {
    return `Payment is due within 30 days of invoice date.
Late payments may incur additional charges.
Please include the invoice number with your payment.`;
  }

  const loadInitialData = async () => {
    try {
      setLoadingData(true);
      const [clientsData, profileData] = await Promise.all([
        clientsService.getClients(),
        profilesService.getProfile(),
      ]);
      setClients(clientsData);
      setProfile(profileData);
    } catch (err) {
      console.error('Error loading initial data:', err);
    } finally {
      setLoadingData(false);
    }
  };

  const loadProjects = async (selectedClientId: string) => {
    try {
      const data = await projectsService.getProjects(selectedClientId);
      setProjects(data);
    } catch (err) {
      console.error('Error loading projects:', err);
    }
  };

  const calculateTotals = () => {
    // Calculate subtotal
    const calculatedSubtotal = lineItems.reduce((sum, item) => sum + item.amount, 0);
    setSubtotal(calculatedSubtotal);

    // Calculate tax
    const calculatedTax = (calculatedSubtotal * taxRate) / 100;
    setTaxAmount(calculatedTax);

    // Calculate total
    const calculatedTotal = calculatedSubtotal + calculatedTax - discount;
    setTotal(Math.max(0, calculatedTotal)); // Ensure total is not negative
  };

  const handleAddLineItem = () => {
    const selectedClient = clients.find((c) => c.id === clientId);
    const defaultUnitPrice = selectedClient?.hourlyRate || 0;
    setLineItems([
      ...lineItems,
      {
        description: '',
        quantity: 1,
        unit: 'hours' as const,
        unitPrice: defaultUnitPrice,
        amount: defaultUnitPrice,
      },
    ]);
  };

  const handleRemoveLineItem = (index: number) => {
    if (lineItems.length === 1) {
      setErrors({ ...errors, lineItems: 'At least one line item is required' });
      return;
    }
    const newLineItems = lineItems.filter((_, i) => i !== index);
    setLineItems(newLineItems);
    setErrors({ ...errors, lineItems: '' });
  };

  const handleLineItemChange = (
    index: number,
    field: keyof Omit<LineItem, 'id'>,
    value: string | number
  ) => {
    const newLineItems = [...lineItems];

    // Handle empty string for numeric fields
    if ((field === 'quantity' || field === 'unitPrice') && value === '') {
      newLineItems[index] = { ...newLineItems[index], [field]: 0 };
    } else {
      newLineItems[index] = { ...newLineItems[index], [field]: value };
    }

    // When the unit changes, re-derive the unit price from the client's hourly rate
    // (e.g. switching to MD bills at hourlyRate × 8). The user can still override it afterwards.
    if (field === 'unit') {
      const selectedClient = clients.find((c) => c.id === clientId);
      const hourlyRate = selectedClient?.hourlyRate || 0;
      newLineItems[index].unitPrice = getRateForUnit(value as LineItemUnit, hourlyRate);
    }

    // Auto-calculate amount
    if (field === 'quantity' || field === 'unitPrice' || field === 'unit') {
      const quantity = field === 'quantity' ? Number(value || 0) : newLineItems[index].quantity;
      const unitPrice = newLineItems[index].unitPrice;
      newLineItems[index].amount = Math.round(quantity * unitPrice * 100) / 100;
    }

    setLineItems(newLineItems);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate client
    if (!clientId) {
      newErrors.clientId = 'Client is required';
    }

    // Validate dates
    if (!issueDate) {
      newErrors.issueDate = 'Issue date is required';
    }
    if (!dueDate) {
      newErrors.dueDate = 'Due date is required';
    }
    if (issueDate && dueDate && new Date(dueDate) <= new Date(issueDate)) {
      newErrors.dueDate = 'Due date must be after issue date';
    }

    // Validate line items
    if (lineItems.length === 0) {
      newErrors.lineItems = 'At least one line item is required';
    }

    lineItems.forEach((item, index) => {
      if (!item.description.trim()) {
        newErrors[`lineItem_${index}_description`] = 'Description is required';
      }
      if (item.quantity <= 0) {
        newErrors[`lineItem_${index}_quantity`] = 'Quantity must be greater than 0';
      }
      if (item.unitPrice < 0) {
        newErrors[`lineItem_${index}_unitPrice`] = 'Unit price cannot be negative';
      }
      if (item.amount <= 0) {
        newErrors[`lineItem_${index}_amount`] = 'Amount must be greater than 0';
      }
    });

    // Validate tax rate
    if (taxRate < 0 || taxRate > 100) {
      newErrors.taxRate = 'Tax rate must be between 0 and 100';
    }

    // Validate discount
    if (discount < 0) {
      newErrors.discount = 'Discount cannot be negative';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (saveAsDraft: boolean = true) => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Ensure line items are properly formatted without id field
      const cleanLineItems = lineItems.map(({ description, quantity, unit, unitPrice, amount }) => ({
        description,
        quantity,
        unit,
        unitPrice,
        amount,
      }));

      const invoiceData: CreateInvoiceRequest = {
        clientId,
        projectId: projectId || undefined,
        issueDate,
        dueDate,
        servicePeriodStart: servicePeriodStart || undefined,
        servicePeriodEnd: servicePeriodEnd || undefined,
        lineItems: cleanLineItems,
        taxRate: taxRate || undefined,
        discount: discount || undefined,
        notes: notes || undefined,
        termsAndConditions: termsAndConditions || undefined,
      };

      let invoice;
      if (mode === 'edit' && initialData?.id) {
        invoice = await invoicesService.updateInvoice(initialData.id, invoiceData);
      } else {
        invoice = await invoicesService.createInvoice(invoiceData);
      }

      // If not saving as draft, send the invoice
      if (!saveAsDraft && invoice) {
        await invoicesService.sendInvoice(invoice.id);
      }

      if (onSuccess && invoice) {
        onSuccess(invoice);
      }
    } catch (err: any) {
      console.error('Error saving invoice:', err);
      setErrors({ submit: err.message || 'Failed to save invoice' });
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-flowky-primary mb-4"></div>
          <p className="text-gray-600">Loading form data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {errors.submit && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
          {errors.submit}
        </div>
      )}

      {/* Client and Project Selection */}
      <div className="border border-border rounded-lg p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Client Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Client Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Client <span className="text-red-500">*</span>
            </label>
            <select
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-flowky-primary focus:border-transparent ${
                errors.clientId ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Select a client</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
            {errors.clientId && <p className="text-red-500 text-sm mt-1">{errors.clientId}</p>}
          </div>

          {/* Project Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Project (Optional)
            </label>
            <select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              disabled={!clientId}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-flowky-primary focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">No project</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Dates */}
      <div className="border border-border rounded-lg p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Invoice Dates</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Issue Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Issue Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={issueDate}
              onChange={(e) => setIssueDate(e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-flowky-primary focus:border-transparent ${
                errors.issueDate ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.issueDate && <p className="text-red-500 text-sm mt-1">{errors.issueDate}</p>}
          </div>

          {/* Due Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Due Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-flowky-primary focus:border-transparent ${
                errors.dueDate ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.dueDate && <p className="text-red-500 text-sm mt-1">{errors.dueDate}</p>}
          </div>

          {/* Service Period Start */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Service Period Start
            </label>
            <input
              type="date"
              value={servicePeriodStart}
              onChange={(e) => setServicePeriodStart(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
            />
          </div>

          {/* Service Period End */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Service Period End
            </label>
            <input
              type="date"
              value={servicePeriodEnd}
              onChange={(e) => setServicePeriodEnd(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Line Items */}
      <div className="border border-border rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Line Items</h3>
          <button
            type="button"
            onClick={handleAddLineItem}
            className="px-3 py-1 text-sm bg-flowky-primary text-white rounded-lg hover:bg-flowky-primary/90 transition-colors flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Item
          </button>
        </div>

        {errors.lineItems && <p className="text-red-500 text-sm mb-4">{errors.lineItems}</p>}

        <div className="space-y-4">
          {lineItems.map((item, index) => (
            <div
              key={index}
              className="p-4 border border-gray-200 rounded-lg bg-white/50 space-y-3"
            >
              <div className="flex items-start justify-between">
                <span className="text-sm font-medium text-gray-700">Item {index + 1}</span>
                {lineItems.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveLineItem(index)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                {/* Description */}
                <div className="md:col-span-5">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={item.description}
                    onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                    placeholder="Service or product description"
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-flowky-primary focus:border-transparent ${
                      errors[`lineItem_${index}_description`] ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors[`lineItem_${index}_description`] && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors[`lineItem_${index}_description`]}
                    </p>
                  )}
                </div>

                {/* Quantity */}
                <div className="md:col-span-1">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Qty <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={item.quantity === 0 ? '' : item.quantity}
                    onChange={(e) => {
                      const value = e.target.value === '' ? '' : parseFloat(e.target.value);
                      if (value === '' || !isNaN(value as number)) {
                        handleLineItemChange(index, 'quantity', value);
                      }
                    }}
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-flowky-primary focus:border-transparent ${
                      errors[`lineItem_${index}_quantity`] ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors[`lineItem_${index}_quantity`] && (
                    <p className="text-red-500 text-xs mt-1">{errors[`lineItem_${index}_quantity`]}</p>
                  )}
                </div>

                {/* Unit */}
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Unit <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={item.unit || 'hours'}
                    onChange={(e) => handleLineItemChange(index, 'unit', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                  >
                    <option value="hours">Hours</option>
                    <option value="md">MD (Man Days)</option>
                    <option value="items">Items</option>
                    <option value="days">Days</option>
                  </select>
                </div>

                {/* Unit Price */}
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Unit Price <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={item.unitPrice || ''}
                    onChange={(e) => {
                      const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                      handleLineItemChange(index, 'unitPrice', isNaN(value) ? 0 : value);
                    }}
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-flowky-primary focus:border-transparent ${
                      errors[`lineItem_${index}_unitPrice`] ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors[`lineItem_${index}_unitPrice`] && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors[`lineItem_${index}_unitPrice`]}
                    </p>
                  )}
                </div>

                {/* Amount (Auto-calculated) */}
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Amount</label>
                  <input
                    type="text"
                    value={`${getCurrencySymbol(clients.find(c => c.id === clientId)?.currency)} ${item.amount.toFixed(2)}`}
                    disabled
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Tax and Discount */}
      <div className="border border-border rounded-lg p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Tax & Discount</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Tax Rate */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tax Rate (%)</label>
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={taxRate}
              onChange={(e) => {
                const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                setTaxRate(isNaN(value) ? 0 : value);
              }}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-flowky-primary focus:border-transparent ${
                errors.taxRate ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.taxRate && <p className="text-red-500 text-sm mt-1">{errors.taxRate}</p>}
          </div>

          {/* Discount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Discount ($)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={discount || ''}
              onChange={(e) => {
                const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                setDiscount(isNaN(value) ? 0 : value);
              }}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-flowky-primary focus:border-transparent ${
                errors.discount ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.discount && <p className="text-red-500 text-sm mt-1">{errors.discount}</p>}
          </div>
        </div>
      </div>

      {/* Totals Summary */}
      <div className="border border-border rounded-lg p-4 bg-gray-50/50">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Invoice Summary</h3>
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-gray-700">Subtotal:</span>
            <span className="text-gray-900 font-medium">${subtotal.toFixed(2)}</span>
          </div>
          {taxRate > 0 && (
            <div className="flex justify-between items-center">
              <span className="text-gray-700">Tax ({taxRate}%):</span>
              <span className="text-gray-900 font-medium">${taxAmount.toFixed(2)}</span>
            </div>
          )}
          {discount > 0 && (
            <div className="flex justify-between items-center">
              <span className="text-gray-700">Discount:</span>
              <span className="text-red-600 font-medium">-${discount.toFixed(2)}</span>
            </div>
          )}
          <div className="pt-2 border-t border-gray-300">
            <div className="flex justify-between items-center">
              <span className="text-lg font-bold text-gray-900">Total:</span>
              <span className="text-2xl font-bold text-flowky-primary">${total.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Notes */}
      <div className="border border-border rounded-lg p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Information</h3>
        <div className="space-y-4">
          {/* Internal Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Internal Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Internal notes (not visible on invoice)"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
            />
          </div>

          {/* Terms and Conditions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Terms and Conditions
            </label>
            <textarea
              value={termsAndConditions}
              onChange={(e) => setTermsAndConditions(e.target.value)}
              rows={4}
              placeholder="Payment terms, late fees, etc."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-3">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
        )}
        <button
          type="button"
          onClick={() => handleSubmit(true)}
          disabled={loading}
          className="px-6 py-2 bg-flowky-primary text-white rounded-lg hover:bg-flowky-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Saving...
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                />
              </svg>
              {mode === 'edit' ? 'Update' : 'Save'}
            </>
          )}
        </button>
      </div>
    </div>
  );
}
